import {AnimatedNumber, AnimatedNumberBuilder, AnimatedNumberFactory} from "../../page/elements/animatedNumber";
import {BufferingShareDataSource, type ShareData, type ShareDataObserver, type ShareDataSource} from "./shareDataSource";
import {resolve} from "../../container";
import {DATETIME_FORMATS, LanguagesService} from "../../common/languages";
import {Dictionary} from "../../page/elements/dictionary";
import type {Nullable} from "../../bootstrap/common/types";
import {html, type TemplateResult} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {UnLitElement} from "../../common/elements";

const MILLISECONDS_FACTOR = 1000;

class ShareTime implements ShareDataObserver {

    public constructor(
        private onReceiveCallback: (dateString: string) => void,
        private languagesService: LanguagesService = resolve(LanguagesService)
    ) {
    }

    public receiveData(data: Nullable<ShareData>): void {
        if (!data) {
            this.onReceiveCallback("");
            return;
        }
        if (data.time === 0) {
            this.onReceiveCallback("0");
            return;
        }

        const langId = this.languagesService.activeLanguage().id;
        const date = new Date(data.time * MILLISECONDS_FACTOR).toLocaleString(langId, {
            ...DATETIME_FORMATS[langId],
            timeZoneName: "short",
            timeZone: "Europe/Berlin"
        });
        this.onReceiveCallback(this.convertToCET(date));
    }

    private convertToCET(dateGMT: string): string {
        return dateGMT.replace("GMT+2", "CEST").replace("GMT+1", "CET");
    }
}

abstract class NumericShareData implements ShareDataObserver {
    private animatedNumber: AnimatedNumber;

    public constructor(animatedNumberBuilder: AnimatedNumberBuilder, element: HTMLElement) {
        this.animatedNumber = this.fineTuneAnimatedNumber(animatedNumberBuilder).appliedTo(element);
    }

    public receiveData(data: Nullable<ShareData>): void {
        const newValue = data ? this.extractNumber(data) : Number.NaN;
        this.animatedNumber.update(newValue);
    }

    protected abstract fineTuneAnimatedNumber(animatedNumber: AnimatedNumberBuilder): AnimatedNumberBuilder;

    protected abstract extractNumber(data: ShareData): number;
}

class ShareValue extends NumericShareData {
    protected extractNumber(data: ShareData): number {
        return data.value;
    }

    protected fineTuneAnimatedNumber(animatedNumber: AnimatedNumberBuilder): AnimatedNumberBuilder {
        return animatedNumber.withFixedPrecision(2);
    }
}

class ShareChange extends NumericShareData {
    protected extractNumber(data: ShareData): number {
        return data.change;
    }

    protected fineTuneAnimatedNumber(animatedNumber: AnimatedNumberBuilder): AnimatedNumberBuilder {
        return animatedNumber
            .withFixedPrecision(2)
            .withNumberGroupSeparator(true)
            .withCustomFormatting((numberValue, stringValue) => (numberValue >= 0 ? "+" : "") + stringValue);
    }
}

class ShareChangeInPercent extends NumericShareData {
    protected extractNumber(data: ShareData): number {
        return data.changePercent;
    }

    protected fineTuneAnimatedNumber(animatedNumber: AnimatedNumberBuilder): AnimatedNumberBuilder {
        return animatedNumber
            .withCustomFormatting((numberValue, stringValue) => (numberValue >= 0 ? "+" : "") + stringValue);
    }
}

@customElement("eop-share-time")
export class EopShareTime extends UnLitElement {

    @state()
    private time: string = "";

    private dictionary: Dictionary;

    public constructor(
        private shareDataSource: ShareDataSource = resolve(BufferingShareDataSource)
    ) {
        super();
        this.dictionary = Dictionary.of(this);
    }

    public connectedCallback(): void {
        super.connectedCallback();
        const dataObserver = new ShareTime(
            dateString => {
                if (dateString === "0") {
                    this.time = this.dictionary.translate("MSG_STOCK_EXCHANGE_CLOSED");
                } else {
                    this.time = dateString;
                }
            }
        );
        if (dataObserver) {
            this.shareDataSource.register(dataObserver);
        }
    }

    public render(): TemplateResult {
        return html`<span class="share-time">${this.time}</span>`;
    }
}

@customElement("eop-share-data")
export class EopShareData extends UnLitElement {

    @property({attribute: "aspect"})
    private aspect: string = "";
    @state()
    private value: string;

    public constructor(
        private shareDataSource: ShareDataSource = resolve(BufferingShareDataSource),
        private animatedNumberFactory: AnimatedNumberFactory = resolve(AnimatedNumberFactory)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        const builder = this.animatedNumberFactory.builder()
            .withStartValue(0)
            .withUpdateCallback(updatedValue => this.value = updatedValue);
        const dispatchDataObserver = (aspect: string): Nullable<ShareDataObserver> => {
            switch (aspect) {
                case "value":
                    return new ShareValue(builder, this);
                case "change":
                    return new ShareChange(builder, this);
                case "changePercent":
                    return new ShareChangeInPercent(builder, this);
                default:
                    return null;
            }
        };

        const dataObserver = dispatchDataObserver(this.aspect);
        if (dataObserver) {
            this.shareDataSource.register(dataObserver);
        }
    }

    public render(): TemplateResult {
        return html`<span class="animated-number">${this.value}</span>`;
    }
}